import { Controller } from "@hotwired/stimulus"

// FD-297/U16: drives the AI assistant drawer. POSTs a turn to the workspace ai-assistant
// endpoint (U15 backend), renders the thread, and in Build mode confirms writes before they
// run. Non-streaming first cut; streaming over Turbo/ActionCable is a later refinement.
export default class extends Controller {
  static targets = ["dialog", "thread", "input", "empty", "mode", "modeLabel", "sendBtn"]
  static values = { url: String }

  connect() {
    this.mode = "ask"
    this.busy = false
    this.updateModeUI()
  }

  // FD-1311: the drawer is a <cs-drawer>, opened and closed through its reflected `open`
  // attribute (which also works before the element has upgraded). The message box carries
  // `autofocus`, which the drawer honours as it opens.
  open() {
    if (this.hasDialogTarget) this.dialogTarget.toggleAttribute("open", true)
  }

  close() {
    if (this.hasDialogTarget) this.dialogTarget.toggleAttribute("open", false)
  }

  // FD-1311: Ask/Build is a cs-radio-group; it reports a pick with `change`.
  setMode() {
    this.mode = this.hasModeTarget && this.modeTarget.value === "build" ? "build" : "ask"
    this.updateModeUI()
  }

  updateModeUI() {
    if (this.hasModeTarget && this.modeTarget.value !== this.mode) this.modeTarget.value = this.mode
    if (this.hasModeLabelTarget) this.modeLabelTarget.textContent = this.mode === "build" ? "Build mode" : "Ask mode"
  }

  // FD-297/U17: opened from the Home "Ask AI" widget — seed the drawer with the
  // question typed there and run it immediately.
  seedFromForm(event) {
    event.preventDefault()
    const field = event.target.querySelector("input, textarea")
    const prompt = (field?.value || "").trim()
    this.open()
    if (!prompt) return
    field.value = ""
    this.appendUser(prompt)
    this.run({ prompt, mode: this.mode })
  }

  sendOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    event.preventDefault()
    this.send(event)
  }

  async send(event) {
    if (event) event.preventDefault()
    const prompt = this.inputTarget.value.trim()
    if (!prompt || this.busy) return
    this.inputTarget.value = ""
    this.appendUser(prompt)
    await this.run({ prompt, mode: this.mode })
  }

  // POST one turn (a fresh prompt, or { confirmation_token } to apply approved writes —
  // FD-459: the server signs the pending calls at needs_confirmation and only that token
  // authorizes execution; the call bodies themselves never round-trip through the client).
  async run(body) {
    if (!this.urlValue) return
    this.setBusy(true)
    const placeholder = this.appendAssistant("…")
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify(body),
      })
      const data = await response.json().catch(() => ({}))
      placeholder.remove()

      if (data.status === "needs_confirmation") {
        this.renderConfirmation(data.pending || [], data.confirmation_token)
      } else if (data.status === "unavailable") {
        this.appendAssistant("The assistant isn't configured on this server yet.")
      } else {
        this.appendAssistant(data.text || "(no response)")
      }
    } catch (_e) {
      placeholder.remove()
      this.appendAssistant("Something went wrong reaching the assistant. Please try again.")
    } finally {
      this.setBusy(false)
    }
  }

  renderConfirmation(pending, confirmationToken) {
    if (pending.length === 0) return
    const card = document.createElement("div")
    card.className = "rounded-lg border border-warning/50 bg-warning/10 p-3 space-y-2"
    const items = pending.map((p) => `<li class="font-mono text-[11px]">${this.escape(p.name)}</li>`).join("")
    card.innerHTML = `
      <p class="text-[12px] font-medium">Confirm ${pending.length} change${pending.length === 1 ? "" : "s"}?</p>
      <ul class="list-disc list-inside text-muted">${items}</ul>
      <div class="flex gap-2 pt-1">
        <cs-button variant="brand" size="xs" data-confirm-run>Run</cs-button>
        <cs-button appearance="plain" size="xs" data-confirm-cancel>Cancel</cs-button>
      </div>`
    card.querySelector("[data-confirm-run]").addEventListener("click", () => {
      card.remove()
      this.run({ confirmation_token: confirmationToken })
    })
    card.querySelector("[data-confirm-cancel]").addEventListener("click", () => {
      card.remove()
      this.appendAssistant("Okay — I won't make those changes.")
    })
    this.threadTarget.appendChild(card)
    this.scrollToEnd()
  }

  appendUser(text) {
    return this.appendBubble(text, "ml-auto bg-primary text-primary-content", "user")
  }

  appendAssistant(text) {
    return this.appendBubble(text, "mr-auto bg-surface border border-outline", "assistant")
  }

  appendBubble(text, classes, role) {
    if (this.hasEmptyTarget) this.emptyTarget.remove()
    const bubble = document.createElement("div")
    bubble.className = `max-w-[85%] rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${classes}`
    bubble.dataset.role = role
    bubble.textContent = text
    this.threadTarget.appendChild(bubble)
    this.scrollToEnd()
    return bubble
  }

  setBusy(busy) {
    this.busy = busy
    if (this.hasSendBtnTarget) this.sendBtnTarget.disabled = busy
  }

  scrollToEnd() {
    this.threadTarget.scrollTop = this.threadTarget.scrollHeight
  }

  escape(value) {
    const span = document.createElement("span")
    span.textContent = String(value)
    return span.innerHTML
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
